/**
 * ``useMetricCatalog`` — the metric-presentation catalog binding + its
 * resolution rules (REQ-STATUS-050 … -057; ADR-035 D6; feature spec
 * ``system-view-elaboration`` §4.9).
 *
 * Selection Detail renders a component's ``domain`` map — a
 * ``map<string, double>`` whose keys are **wire contract** — and up to Phase E
 * it put the raw key in front of the operator (``rawblob_finalize_queue_depth``,
 * ``authorize_total``). This module is the client half of the display layer that
 * turns a ``(component_kind, metric_key)`` pair into a human label and a
 * descriptive tooltip.
 *
 * # The catalog has no authority
 *
 * It annotates; it can never rename a wire key, hide a metric, reorder one out
 * of view, gate one behind a role, or influence a threshold, a rollup or a
 * stoplight (REQ-STATUS-054). Two properties enforce that on this side of the
 * wire:
 *
 *   - :func:`resolveDomainMetric` / :func:`resolveResourceMetric` return
 *     ``null`` for anything they cannot resolve, and every caller renders the
 *     **raw key** in that case. A missing entry therefore degrades to the
 *     pre-Phase-E display, never to a blank cell, a hidden row, or a hardcoded
 *     stand-in label. The domain map is open-ended by design, so a service that
 *     gains a metric shows it *immediately* — unlabelled but visible — rather
 *     than vanishing until someone edits a file;
 *   - both resolvers accept an ``undefined`` catalog and answer ``null``. That
 *     is what makes the loading, error and unauthorized paths render honest raw
 *     keys instead of an empty panel: a failed catalog fetch costs the operator
 *     the prose, never the numbers.
 *
 * # Keyed by the pair, not by the metric
 *
 * ``connections_active``, ``decode_errors_total``, ``write_errors_total`` and
 * ``lease_active`` each mean different things on different components (egress
 * consumer sockets vs. Postgres backend sessions; NATS message decode vs.
 * status-report decode). A flat metric → label map would put a confidently
 * wrong sentence in front of an operator, so the key is the pair
 * (REQ-STATUS-053) and resolution runs **exact → pattern → global**, mirroring
 * :mod:`meridian_bff._metric_catalog` step for step. ``ResourceMetrics`` fields
 * are typed struct members rather than domain-map keys, so they resolve in
 * their own namespace and never fall through to ``global``.
 *
 * # Fetching
 *
 * One authenticated, session-gated read of
 * :data:`METRIC_CATALOG_PATH` through the shared ``apiFetch`` client — never a
 * bare ``fetch`` and never a static asset under ``ui/public/``: ``apiFetch``
 * rejects absolute URLs, which is the mechanism that keeps REQ-UI-035's
 * zero-non-BFF-calls guarantee true, and nginx would serve a ``public/`` file
 * from outside the session gate (REQ-STATUS-055). The document is deployment
 * **static** — the BFF reads it once at startup and never hot-reloads it
 * (REQ-STATUS-051) — so it is cached for the lifetime of the tab and refetches
 * on nothing.
 *
 * Cross-references:
 *   - REQ-STATUS-050 (labels + tooltips), -051 (startup load), -053 (pair
 *     keying + resolution order), -054 (raw-key fallback), -055 (authenticated
 *     all-roles delivery), -057 (wire keys unchanged; test ids keyed on the
 *     metric key).
 *   - REQ-UI-035 (BFF-only calls); REQ-UI-026 (TanStack Query owns server state).
 *   - ``packages/meridian_bff/src/meridian_bff/_metric_catalog.py`` — the loader
 *     and the resolution order this file mirrors.
 */
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api-client";
import { useSession } from "@/auth/session-context";
import type { components } from "@/lib/api-types.gen";
import type { MetricUnit } from "@/system-status/metric-units";
import { SYSTEM_STATUS_READER_ROLES } from "@/system-status/use-system-status-snapshot";

/** The served catalog document (generated from the BFF's ``MetricCatalog``). */
export type MetricCatalog = components["schemas"]["MetricCatalog"];

/** Presentation for one metric: what it is called and what it means. */
export type MetricEntry = components["schemas"]["MetricEntry"];

/** Presentation for a dynamic metric family, e.g. MinIO's per-bucket keys. */
export type MetricPattern = components["schemas"]["MetricPattern"];

/**
 * A metric's resolved presentation.
 *
 * ``threshold`` was surfaced on 2026-08-03 (architect) — the anticipated
 * additive widening. It is present only on the rollup-driving metrics (the
 * exact field set of the collector's ``StatusThresholds``, pinned by the
 * REQ-STATUS-058 coverage guard), and it is the SAME pair the collector
 * evaluates. That identity is the whole point: the display marks a metric as
 * the one pushing the rollup by re-evaluating the collector's own numbers,
 * rather than by parsing the rollup's reason sentence.
 *
 * ``unit`` was surfaced on 2026-08-03 (REQ-STATUS-075) — the second such
 * widening, and for the same reason as the first: it gained a consumer. The
 * trend detail chart cannot label an axis before the unit is available, and a
 * value read off a crosshair means nothing without one. Until then nothing
 * formatted by it, and an unused knob is still an unused knob (CLAUDE.md §10).
 */
export interface ResolvedMetric {
  /** Human label for the row. Rendered as text, never markup. */
  label: string;
  /** Descriptive tooltip for the row. Rendered as text, never markup. */
  tooltip: string;
  /**
   * The collector's ``{warn, crit}`` upper bound, on the metrics that can drive
   * the stoplight. Absent on every other metric — and absence must read as
   * "cannot escalate", never as "within bounds".
   */
  threshold?: { warn: number; crit: number };
  /**
   * The catalogued presentation unit, driving axis labels and value formatting
   * (:mod:`@/system-status/metric-units`).
   *
   * **Absent stays absent.** A metric the catalog does not describe gets no
   * default unit: ``count`` on an unlabelled metric would be a claim about what
   * the number *is*, and the catalog holds no authority over that
   * (REQ-STATUS-054). :func:`unitKind` routes ``undefined`` to the plain
   * numeric path, which is the honest degradation.
   *
   * ⚠ Two of the eight values — ``boolean`` and ``epoch_seconds`` — are **not
   * magnitudes** and must not reach a min/mid/max axis (REQ-STATUS-075 §9.1).
   * Callers route on :func:`unitKind` rather than switching on this directly.
   */
  unit?: MetricUnit;
}

/**
 * A metric's contribution to the rollup, from its value and its threshold.
 *
 * Mirrors ``meridian_status_collector._thresholds.threshold_state`` exactly,
 * including that both bounds are **inclusive upper** bounds (``>=``): a value
 * sitting precisely on `crit` is unhealthy in the collector, so it must be
 * unhealthy here too, or the display and the stoplight disagree at the boundary
 * — which is the one place an operator is most likely to be looking.
 *
 * Returns ``null`` where the metric cannot escalate at all, which is different
 * from "healthy" and is why this is not a boolean.
 */
export function metricThresholdState(
  value: number | null | undefined,
  resolved: ResolvedMetric | null | undefined,
): "degraded" | "unhealthy" | null {
  const threshold = resolved?.threshold;
  if (threshold === undefined || value === null || value === undefined) return null;
  if (value >= threshold.crit) return "unhealthy";
  if (value >= threshold.warn) return "degraded";
  return null;
}

/**
 * The catalog route (REQ-STATUS-055 amended 2026-08-01).
 *
 * A dedicated endpoint in the **config** namespace rather than a sibling key on
 * ``GET /api/config``: that response is fetched at app boot and the
 * classification banner blocks on it, so folding ~90 label/description entries
 * into it would charge every session a first-paint cost for a surface most
 * sessions never open. The sub-path inherits the parent's session gate — the
 * BFF's middleware skip-list is prefix-matched and ``/api/config`` is not on it.
 */
export const METRIC_CATALOG_PATH = "/api/config/system-status-metrics-catalog";

/** TanStack Query key for the catalog. One entry per tab; nothing invalidates it. */
export const METRIC_CATALOG_KEY: readonly unknown[] = ["system-status", "metric-catalog"];

/**
 * Fetch the deployment's metric-presentation catalog.
 *
 * Cached **aggressively and deliberately**: the BFF reads the document once at
 * startup into an immutable map and offers no hot reload (REQ-STATUS-051), so a
 * refetch could only ever return the identical bytes. ``staleTime`` /
 * ``gcTime`` of ``Infinity`` plus the three ``refetchOn*: false`` flags mean one
 * request per tab, shared by every Selection Detail that mounts — the panel is
 * opened and closed repeatedly during triage, and re-reading ~90 static entries
 * on each mount would be pure waste.
 *
 * ``enabled`` mirrors the route's own ``require_roles`` over all four Meridian
 * roles, so a session carrying no known role never issues a request the BFF
 * would answer with 403 (defence-in-depth; the BFF remains the enforcement
 * point per ADR-009). A denied or failed fetch is **not** an error state the UI
 * surfaces: the resolvers answer ``null`` for an ``undefined`` catalog and the
 * rows fall back to raw keys (REQ-STATUS-054).
 */
export function useMetricCatalog(): UseQueryResult<MetricCatalog, ApiError> {
  const session = useSession();
  return useQuery<MetricCatalog, ApiError>({
    queryKey: METRIC_CATALOG_KEY,
    // The catalog route declares the identical four-role set the snapshot read
    // does (``_metric_catalog_routes.py``); one constant keeps the two gates
    // from drifting apart (CLAUDE.md §4.4).
    enabled: session.hasAnyRole(SYSTEM_STATUS_READER_ROLES),
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>(METRIC_CATALOG_PATH, { signal });
      if (!isMetricCatalog(raw)) {
        console.error(
          "[meridian/system-status/use-metric-catalog] response failed shape validation",
          raw,
        );
        throw new ApiError(500, raw, "metric catalog response did not match the expected shape");
      }
      return raw;
    },
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // The BFF's RBAC verdict is authoritative — retrying a 403 just repeats a
      // denial the operator cannot resolve by waiting.
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Resolve one ``domain``-map key: exact → pattern → global (REQ-STATUS-053).
 *
 * @param catalog - The served catalog, or ``undefined`` while it is loading,
 *   failed, or gated off. ``undefined`` is not an error here — it resolves to
 *   ``null`` and the caller renders the raw key (REQ-STATUS-054).
 * @param componentKind - The lowercase ``ComponentKind`` token carried on
 *   ``ComponentModel.component_kind``. An unknown token is not an error: it
 *   simply matches no per-kind section, so resolution falls through to
 *   ``global``.
 * @param metricKey - The raw wire key, unchanged. Never rewritten
 *   (REQ-STATUS-057).
 * @returns The resolved presentation, or ``null`` when nothing matches — a
 *   rendering rule, never a dropped row.
 */
export function resolveDomainMetric(
  catalog: MetricCatalog | undefined,
  componentKind: string,
  metricKey: string,
): ResolvedMetric | null {
  if (catalog === undefined) return null;
  const exact = catalog.components?.[componentKind]?.[metricKey];
  if (exact !== undefined) return presentation(exact);
  for (const pattern of catalog.patterns?.[componentKind] ?? []) {
    const resolved = matchPattern(pattern, metricKey);
    if (resolved !== null) return resolved;
  }
  const fallback = catalog.global?.[metricKey];
  return fallback === undefined ? null : presentation(fallback);
}

/**
 * Resolve a typed ``ResourceMetrics`` field in its own namespace.
 *
 * Resource fields are struct members shared by every component kind rather than
 * domain-map keys, so they never collide across kinds and — unlike a domain key
 * — never fall through to ``global`` (REQ-STATUS-053). An unknown field
 * resolves to ``null`` and the caller renders the raw field name.
 */
export function resolveResourceMetric(
  catalog: MetricCatalog | undefined,
  fieldName: string,
): ResolvedMetric | null {
  if (catalog === undefined) return null;
  const entry = catalog.resource?.[fieldName];
  return entry === undefined ? null : presentation(entry);
}

/**
 * Narrow one served entry to the fields the UI renders.
 *
 * Both optional fields are spread conditionally rather than assigned: the
 * served shape makes them nullable, and ``{ unit: null }`` would defeat every
 * ``unit === undefined`` check downstream while still looking absent in a
 * console log.
 */
function presentation(entry: MetricEntry): ResolvedMetric {
  const threshold = entry.threshold;
  return {
    label: entry.label,
    tooltip: entry.tooltip,
    ...(threshold ? { threshold: { warn: threshold.warn, crit: threshold.crit } } : {}),
    ...(entry.unit ? { unit: entry.unit } : {}),
  };
}

/**
 * Try one dynamic-family rule against a key, substituting the captured token.
 *
 * Mirrors the BFF's compile step exactly: the single ``{token}`` becomes
 * ``([^_]+)`` between the escaped literal prefix and suffix, anchored at both
 * ends. ``[^_]`` rather than ``.`` is what makes ``bucket_{bucket}_bytes``
 * unambiguous — MinIO bucket names are lowercase alphanumerics, hyphens and
 * dots, never underscores — and it is non-backtracking, so an operator-supplied
 * ``match`` cannot become a ReDoS surface on an authenticated page.
 */
function matchPattern(pattern: MetricPattern, metricKey: string): ResolvedMetric | null {
  const compiled = compile(pattern.match);
  if (compiled === null) return null;
  const captured = compiled.regex.exec(metricKey);
  if (captured === null) return null;
  const value = captured[1];
  return {
    label: substitute(pattern.label, compiled.placeholder, value),
    tooltip: substitute(pattern.tooltip, compiled.placeholder, value),
    // The unit belongs to the FAMILY, not the captured token, so it is carried
    // verbatim. Dropping it here would have been the silent half of this
    // widening: MinIO's per-bucket sizes are the largest `bytes` metrics in the
    // catalog, and unitless they render as ten-digit numbers.
    ...(pattern.unit ? { unit: pattern.unit } : {}),
  };
}

/**
 * Replace every occurrence of the placeholder with the captured token.
 *
 * ``split``/``join`` rather than ``String.replace``: the latter replaces only
 * the first occurrence (the BFF's ``str.replace`` replaces all), and it treats
 * ``$`` sequences in the replacement as capture-group references.
 */
function substitute(template: string, placeholder: string, value: string): string {
  return template.split(placeholder).join(value);
}

/** A compiled ``{token}`` template: its full-match regex and its placeholder. */
interface CompiledPattern {
  regex: RegExp;
  placeholder: string;
}

/**
 * Memoized compile cache. The catalog is immutable for the life of the tab and
 * Selection Detail resolves every visible key on each render, so compiling per
 * render would be repeated work with a fixed, tiny key space (the shipped
 * catalog declares two patterns).
 */
const COMPILED = new Map<string, CompiledPattern | null>();

const TOKEN_RE = /\{[^{}]*\}/;

/** Compile a ``{token}`` template, or ``null`` when it holds no token. */
function compile(match: string): CompiledPattern | null {
  const cached = COMPILED.get(match);
  if (cached !== undefined) return cached;
  const found = TOKEN_RE.exec(match);
  // The BFF rejects a token-less ``match`` at startup (REQ-STATUS-052), so this
  // is unreachable against a validated catalog; answering null rather than
  // throwing keeps a hypothetical drift costing the operator a label, not the
  // panel.
  const compiled: CompiledPattern | null =
    found === null
      ? null
      : {
          placeholder: found[0],
          regex: new RegExp(
            `^${escapeRegExp(match.slice(0, found.index))}([^_]+)${escapeRegExp(
              match.slice(found.index + found[0].length),
            )}$`,
          ),
        };
  COMPILED.set(match, compiled);
  return compiled;
}

/** Escape every regex metacharacter in a literal fragment of a ``match`` template. */
function escapeRegExp(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Trust-boundary shape guard (coding-standards §2.2).
 *
 * Checks the envelope, not every entry: a served document has already passed
 * the BFF's Pydantic validation, so the failure this guards against is *drift*
 * — a differently-shaped body reaching a renderer. A rejected body surfaces as
 * a query error, which the resolvers turn into raw-key rows rather than a
 * broken panel.
 */
function isMetricCatalog(raw: unknown): raw is MetricCatalog {
  if (!isRecord(raw)) return false;
  if (!isOptionalRecord(raw.resource) || !isOptionalRecord(raw.global)) return false;
  if (!isOptionalRecord(raw.components) || !isOptionalRecord(raw.patterns)) return false;
  if (isRecord(raw.components) && !Object.values(raw.components).every(isRecord)) return false;
  if (isRecord(raw.patterns) && !Object.values(raw.patterns).every(Array.isArray)) return false;
  return true;
}

/** Whether a value is a non-null, non-array object. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Whether a value is absent or a record — every catalog section is optional. */
function isOptionalRecord(value: unknown): boolean {
  return value === undefined || isRecord(value);
}
